import React, { useEffect, useRef } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useNavigate } from 'react-router-dom';
import {} from 'tamagui';
import { analyticsService } from '@cobu/analytics';

import { useEvents } from '../hooks/useEvents';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Gathering, EventFilterType } from '@cobu/types';
import { CobuStack, CobuText, CobuContainer, CobuList, EventsListSkeleton } from '@cobu/ui';
import { formatDateTime } from '../utils/dateTime';
import {
  AddEventFloatingButton,
  EventEmptyState,
  EventItem,
  EventFeedbackDialogContainer,
} from '../components/events';
import { Strings } from '../constants/Strings';
import { showErrorMessage } from '../utils/messageToast';
import { PullToRefreshWrapper } from '../components/ui/PullToRefreshWrapper';

// Get strings from central repository
const strings = Strings.get();

export default function EventsScreen() {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);

  const {
    filteredGatherings,
    filteredGatheringsCount,
    filters,
    currentFilter,
    setCurrentFilter,
    isLoading,
    isRefreshing,
    isError,
    refreshEvents,
    isEventLive,
    openNotificationCenter,
    getEmptyStateConfig,
  } = useEvents(user?.building?.uuid || '');

  useEffect(() => {
    analyticsService.trackEvent('VIEW_EVENT_FEED', {
      buildingName: user?.building?.name,
    });
  }, [user?.building?.name]);

  // Header now configured in layout

  // const renderRefreshControl = () => (
  //   <RefreshControl
  //     refreshing={isRefreshing}
  //     onRefresh={refreshEvents}
  //     tintColor="$cobuDarkBlue"
  //   />
  // );

  const renderHeader = () =>
    renderFilterButtons({
      currentFilter,
      handleFilterPress: setCurrentFilter,
      filters,
    });

  const renderGatheringItem = ({ item: gathering }: { item: Gathering }) => {
    return (
      <EventItem
        gathering={gathering}
        currentFilter={currentFilter}
        isLive={isEventLive(gathering)}
        onPress={() => {
          // Pass eventDate to ensure timing matches between list and detail pages
          // The detail page will handle 400 errors for non-recurring events by retrying without eventDate
          const eventDate = formatDateTime(
            gathering.scheduledStartTime,
            gathering.buildingTimezone || '',
            'YYYY-MM-DD'
          );
          navigate(`/events/${gathering.uuid}?eventDate=${eventDate}`);
        }}
      />
    );
  };

  const renderEmptyState = () => {
    const { title, description } = getEmptyStateConfig();

    return (
      <EventEmptyState
        title={title}
        description={description}
      />
    );
  };

  const renderSectionHeader = () => (
    <CobuStack mx="$4" my="$6">
      <CobuText variant="subheading" fow="$semibold" color="$cobuDarkBlue">
        {strings.events.list.sectionTitle} {filteredGatheringsCount > 0 && `(${filteredGatheringsCount})`}
      </CobuText>
    </CobuStack>
  );

  // Web: simple pull-to-refresh can be handled by browser; provide a manual refresh button elsewhere if needed

  const itemContent = (_index: number, item: Gathering) => renderGatheringItem({ item });
  const computeItemKey = (_index: number, item: Gathering) => `${item.uuid}-${item.scheduledStartTime}-${_index}`;

  const renderEventsContent = () => {
    if (isError) {
      showErrorMessage(strings.common.somethingWentWrong);
      return null;
    }

    if (isLoading) {
      return <EventsListSkeleton />;
    }

    // Main content - virtualized list for performance
    return (
      <>
        {renderSectionHeader()}
        {filteredGatherings.length === 0 ? (
          renderEmptyState()
        ) : (
          <Virtuoso
            data={filteredGatherings}
            itemContent={itemContent}
            computeItemKey={computeItemKey}
            useWindowScroll
            style={{ width: '100%' }}
            components={{
              Footer: () => <div style={{ height: '80px', width: '100%' }} />,
            }}
          />
        )}
      </>
    );
  };

  return (
    <CobuContainer flex={1} bg="$white">
      <PullToRefreshWrapper
        onRefresh={refreshEvents}
        isRefreshing={isRefreshing}
        useWindowScroll={true}
      >
        {renderHeader()}
        {renderEventsContent()}
      </PullToRefreshWrapper>

      <AddEventFloatingButton />
      <EventFeedbackDialogContainer />
    </CobuContainer>
  );
}

function renderFilterButtons({
  currentFilter,
  handleFilterPress,
  filters,
}: {
  currentFilter: EventFilterType;
  handleFilterPress: (filter: EventFilterType) => void;
  filters: { filterType: EventFilterType; filterText: string }[];
}) {
  return (
    <CobuStack layout="horizontal" gap="$0" mx="$4" mt="$3">
      {filters.map(filter => (
        <CobuStack
          key={filter.filterType}
          f={1}
          mih={'$7'}
          h="auto"
          ai="center"
          jc="center"
          bg={currentFilter === filter.filterType ? '$cobuDarkBlue' : 'transparent'}
          br="$6"
          onPress={() => handleFilterPress(filter.filterType)}
          role="button"
          tabIndex={0}
          style={{ 
            WebkitTapHighlightColor: 'transparent' as any,
            minWidth: 0,
            flexBasis: 0,
          }}
        >
          <CobuText
            color={currentFilter === filter.filterType ? '$white' : '$cobuDarkBlue'}
            fontWeight="500"
            fontSize="$2"
            maxFontSizeMultiplier={1.3}
            numberOfLines={1}
          >
            {filter.filterText}
          </CobuText>
        </CobuStack>
      ))}
    </CobuStack>
  );
}
